import { axiosObject } from "../config";

export interface StateCityResult {
  uuid: string;
  label: string;
  value: string;
}

export interface ResolveStateCityResponse {
  state: StateCityResult | null;
  city: StateCityResult | null;
  error?: string;
}

interface StateCityInput {
  uuid?: string;
  label?: string;
  value?: string;
  name?: string;
}

// Helper to convert name to slug format
const toSlug = (name: string): string => {
  return name.toLowerCase().trim().replace(/\s+/g, "-");
};

// Find state by name (case-insensitive)
const findStateByName = async (stateName: string): Promise<any> => {
  const uri = "auction/data/resource/states/_read";
  const payload = {
    filterBy: [
      {
        attribute: "name",
        op: "eq",
        value: stateName.toLowerCase().trim(),
      },
    ],
  };
  
  const response = await axiosObject.post(uri, payload);
  return response.data?.data?.[0] || null;
};

// Create a new state
const createState = async (stateName: string): Promise<any> => {
  const uri = "auction/data/resource/states/_create";
  const payload = {
    name: stateName.toLowerCase().trim(),
  };
  
  const response = await axiosObject.post(uri, payload);
  return response.data;
};

// Find city by name and state_uuid
const findCityByNameAndState = async (
  cityName: string,
  stateUuid: string
): Promise<any> => {
  const uri = "auction/data/resource/cities/_read";
  const payload = {
    filterBy: [
      {
        attribute: "name",
        op: "eq",
        value: cityName.toLowerCase().trim(),
      },
      {
        attribute: "state_uuid",
        op: "eq",
        value: stateUuid,
      },
    ],
  };
  
  const response = await axiosObject.post(uri, payload);
  return response.data?.data?.[0] || null;
};

// Create a new city
const createCity = async (
  cityName: string,
  stateUuid: string,
  districtUuid?: string
): Promise<any> => {
  const uri = "auction/data/resource/cities/_create";
  const payload: any = {
    name: cityName.toLowerCase().trim(),
    state_uuid: stateUuid,
  };
  
  // district_uuid is required for city creation
  if (districtUuid) {
    payload.district_uuid = districtUuid;
  }
  
  const response = await axiosObject.post(uri, payload);
  return response.data;
};

// Find district by name (case-insensitive)
const findDistrictByName = async (districtName: string): Promise<any> => {
  const uri = "auction/data/resource/districts/_read";
  const payload = {
    filterBy: [
      {
        attribute: "name",
        op: "eq",
        value: districtName.toLowerCase().trim(),
      },
    ],
  };
  
  const response = await axiosObject.post(uri, payload);
  return response.data?.data?.[0] || null;
};

// Create a new district
const createDistrict = async (
  districtName: string,
  stateUuid: string
): Promise<any> => {
  const uri = "auction/data/resource/districts/_create";
  const payload = {
    name: districtName.toLowerCase().trim(),
    state_uuid: stateUuid,
  };
  
  const response = await axiosObject.post(uri, payload);
  return response.data;
};

// Main function to resolve state and city
// districtName is optional - only needed when creating a new city
export default async function resolveStateAndCity(
  state: StateCityInput | string | null,
  city: StateCityInput | string | null,
  districtName?: string
): Promise<ResolveStateCityResponse> {
  try {
    // Handle state
    let stateResult: StateCityResult | null = null;

    if (!state) {
      // No state provided
    } else if (typeof state === "object" && state.uuid) {
      // Already has UUID - return as-is
      stateResult = {
        uuid: state.uuid,
        label: state.label || state.value || "",
        value: state.value || state.label || state.uuid,
      };
    } else {
      // Need to find or create state
      const stateName =
        typeof state === "string"
          ? state
          : state.name || state.label || state.value || "";

      if (stateName) {
        // Try to find existing state
        let existingState = await findStateByName(stateName);

        if (!existingState) {
          // Create new state if not found
          const created = await createState(stateName);
          if (created?.uuid) {
            existingState = {
              uuid: created.uuid,
              name: stateName,
              slug: toSlug(stateName),
            };
          }
        }

        if (existingState) {
          stateResult = {
            uuid: existingState.uuid,
            label: existingState.name || stateName,
            value: existingState.slug || toSlug(stateName),
          };
        }
      }
    }

    // Handle city (needs state_uuid first)
    let cityResult: StateCityResult | null = null;

    if (!city) {
      // No city provided
    } else if (typeof city === "object" && city.uuid) {
      // Already has UUID - return as-is
      cityResult = {
        uuid: city.uuid,
        label: city.label || city.value || "",
        value: city.value || city.label || city.uuid,
      };
    } else if (stateResult?.uuid) {
      // Need to find or create city (only if we have state_uuid)
      const cityName =
        typeof city === "string"
          ? city
          : city.name || city.label || city.value || "";

      if (cityName) {
        // Try to find existing city
        let existingCity = await findCityByNameAndState(
          cityName,
          stateResult.uuid
        );

        if (!existingCity) {
          // Need to find or create district first (required for city creation)
          let districtUuid: string | null = null;
          
          if (districtName) {
            // Try to find existing district
            let existingDistrict = await findDistrictByName(districtName);
            
            if (!existingDistrict) {
              // Create new district if not found
              const createdDistrict = await createDistrict(districtName, stateResult.uuid);
              if (createdDistrict?.uuid) {
                districtUuid = createdDistrict.uuid;
              }
            } else {
              districtUuid = existingDistrict.uuid;
            }
          }
          
          // Create new city with district_uuid (if available)
          const created = await createCity(cityName, stateResult.uuid, districtUuid || undefined);
          if (created?.uuid) {
            existingCity = {
              uuid: created.uuid,
              name: cityName,
              slug: toSlug(cityName),
            };
          } else if (created?.errorCode) {
            // City creation failed (e.g., missing district) - return error
            return {
              state: stateResult,
              city: null,
              error: created.message || "Failed to create city",
            };
          }
        }

        if (existingCity) {
          cityResult = {
            uuid: existingCity.uuid,
            label: existingCity.name || cityName,
            value: existingCity.slug || toSlug(cityName),
          };
        }
      }
    }

    return {
      state: stateResult,
      city: cityResult,
    };
  } catch (error: any) {
    console.error("Error in resolveStateAndCity:", error);
    return {
      state: null,
      city: null,
      error: error.message || "Failed to resolve state/city",
    };
  }
}
